import { useRef, type ReactNode } from 'react';
import { UploadSimpleIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Textarea } from 'src/components/ui/textarea';

type ReadResult = { ok: true; bundle: unknown } | { ok: false; error: string };

interface JsonFileTextareaProps {
  value: string;
  /** Text edited or loaded; a load always clears the error. */
  onChange: (text: string) => void;
  onError: (error: string) => void;
  /** Read + validate a picked file (e.g. `readSopBundleFile`). */
  readFile: (file: File) => Promise<ReadResult>;
  placeholder: string;
  loadLabel: ReactNode;
}

/**
 * The input half of every "import from JSON" surface (procedures, agents): paste JSON, or load
 * an exported `.json` file, which is validated and shown pretty-printed for review before import.
 */
export function JsonFileTextarea({
  value,
  onChange,
  onError,
  readFile,
  placeholder,
  loadLabel,
}: JsonFileTextareaProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const result = await readFile(file);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    onChange(JSON.stringify(result.bundle, null, 2));
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(event) => void handleFileChange(event)}
      />
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadSimpleIcon className="mr-2 h-4 w-4" />
          {loadLabel}
        </Button>
      </div>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={14}
        className="font-mono text-xs"
      />
    </>
  );
}
